import { Box, Typography } from '@mui/material';
import { Upload } from 'lucide-react';
import { useIntl } from 'react-intl';
import type { DiskImport } from '../hooks';
import { QuietButton, RaisedButton } from '~/containers/patient-list/shared';
import ImportEmptyState from './ImportEmptyState';

type ImportDropzoneProps = {
    isDragging: boolean;
    /** Why the last drop was not taken; shown under the hint. */
    refusal?: string;
    picker: DiskImport['picker'];
};

/**
 * The file review's empty state: the drop target and the two file pickers. The pickers' inputs are
 * the dialog's own, so the same pickers open from wherever the dialog offers them.
 */
export default function ImportDropzone({
    isDragging,
    refusal,
    picker,
}: ImportDropzoneProps) {
    const intl = useIntl();
    return (
        // Dashed because dashed means "an empty slot, put something here" in this design. The DICOM
        // source's empty state carries no such frame on purpose: nothing can be dropped into it.
        <Box
            sx={{
                flex: 1,
                minHeight: 0,
                display: 'flex',
                m: 2,
                border: 1,
                borderStyle: 'dashed',
                borderColor: isDragging ? 'primary.main' : 'divider',
                borderRadius: 1,
            }}
        >
            <ImportEmptyState
                graphic={
                    <Box
                        sx={{
                            width: 60,
                            height: 60,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'primary.light',
                            bgcolor: 'action.selected',
                        }}
                    >
                        <Upload size={26} />
                    </Box>
                }
                title={intl.formatMessage({
                    id: 'patientList.import.dropzone.heading',
                })}
                actions={
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '10px',
                        }}
                    >
                        <Typography
                            sx={{ fontSize: 12, color: 'text.disabled' }}
                        >
                            {intl.formatMessage({
                                id: 'patientList.import.dropzone.or',
                            })}
                        </Typography>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                            }}
                        >
                            <RaisedButton
                                onClick={picker.chooseFolder}
                                sx={{ px: 2.25, py: 1, fontSize: 13 }}
                            >
                                {intl.formatMessage({
                                    id: 'patientList.import.dropzone.chooseFolder',
                                })}
                            </RaisedButton>
                            <QuietButton
                                onClick={picker.chooseFiles}
                                sx={{ px: 1.25, py: 1, fontSize: 13 }}
                            >
                                {intl.formatMessage({
                                    id: 'patientList.import.dropzone.chooseFiles',
                                })}
                            </QuietButton>
                        </Box>
                    </Box>
                }
                description={intl.formatMessage({
                    id: 'patientList.import.dropzone.hint',
                })}
                footer={
                    refusal === undefined ? undefined : (
                        <Typography
                            role="alert"
                            sx={{
                                fontSize: 12,
                                color: 'error.main',
                                maxWidth: 420,
                            }}
                        >
                            {refusal}
                        </Typography>
                    )
                }
            />
        </Box>
    );
}
